// services/nhlService.js
class NHLService {
  constructor() {
    this.baseURL = 'https://v1.hockey.api-sports.io';
    this.apiKey = process.env.REACT_APP_FOOTBALL_API_KEY; // Same API key as football/NFL
    this.debug = false; // Set to true for API debugging
    this.cache = {};

    if (!this.apiKey) {
      console.warn('🏒 NHL API key not found in environment variables');
    }
  }

  // Method to manually set API key if env vars aren't working
  setApiKey(key) {
    this.apiKey = key;
    this.log('API key manually set:', { keyLength: key ? key.length : 0 });
  }

  log(message, data = null) {
    if (this.debug) {
      console.log(`[NHL API] ${message}`, data || '');
    }
  }

  // Cache management
  getCacheKey(endpoint, params) {
    return `nhl_${endpoint}_${JSON.stringify(params)}`;
  }

  setCache(key, data, ttl = 300000) { // 5 minutes default TTL
    this.cache[key] = {
      data,
      timestamp: Date.now(),
      ttl
    };
  }

  getCache(key) {
    const cached = this.cache[key];
    if (!cached) return null;

    if (Date.now() - cached.timestamp > cached.ttl) {
      delete this.cache[key];
      return null;
    }

    return cached.data;
  }

  clearCache() {
    this.cache = {};
    this.log('Cache cleared');
  }

  async makeRequest(endpoint, params = {}) {
    // Check cache first
    const cacheKey = this.getCacheKey(endpoint, params);
    const cachedData = this.getCache(cacheKey);
    if (cachedData) {
      this.log(`Cache hit for ${endpoint}`, params);
      return cachedData;
    }

    this.log(`Making request to ${endpoint}`, params);

    if (!this.apiKey) {
      throw new Error(`NHL API key not configured.

Please configure the API key:
1. Add REACT_APP_FOOTBALL_API_KEY to your .env file (same level as package.json)
2. Restart your development server after adding the variable
3. Or use NHLService.setApiKey('your_key_here') as a temporary workaround

Note: NHL uses the same API key as Football/NFL (api-sports.io)`);
    }

    const url = new URL(`${this.baseURL}${endpoint}`);

    // Add query parameters
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        url.searchParams.append(key, params[key]);
      }
    });

    this.log(`Request URL: ${url.toString()}`);

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'x-rapidapi-key': this.apiKey,
          'x-rapidapi-host': 'v1.hockey.api-sports.io'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        this.log(`API Error Response:`, errorText);
        throw new Error(`NHL API Error: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      this.log(`Response received:`, {
        results: data.results,
        errors: data.errors
      });

      // Cache the successful response
      if (data && !data.errors?.length) {
        this.setCache(cacheKey, data);
      }

      return data;
    } catch (error) {
      this.log(`Request failed:`, error);
      throw error;
    }
  }

  // ===== TIMEZONES =====
  async getTimezones() {
    return await this.makeRequest('/timezone');
  }

  // ===== SEASONS =====
  async getSeasons() {
    return await this.makeRequest('/seasons');
  }

  // ===== COUNTRIES =====
  async getCountries(params = {}) {
    return await this.makeRequest('/countries', params);
  }

  // ===== LEAGUES =====
  async getLeagues(params = {}) {
    return await this.makeRequest('/leagues', params);
  }

  // ===== TEAMS =====
  async getTeams(params = {}) {
    if (!params.id && !params.name && !params.league && !params.season && !params.country && !params.search) {
      throw new Error('At least one parameter is required for teams endpoint');
    }
    return await this.makeRequest('/teams', params);
  }

  // ===== GAMES =====
  async getGames(params = {}) {
    if (!params.id && !params.date && !params.league && !params.season && !params.team) {
      throw new Error('At least one parameter is required for games endpoint');
    }
    return await this.makeRequest('/games', params);
  }

  // Convenience method: Get games by date
  async getGamesByDate(date, league = null, season = null, timezone = null) {
    const params = { date };
    if (league) params.league = league;
    if (season) params.season = season;
    if (timezone) params.timezone = timezone;
    return await this.getGames(params);
  }

  // Convenience method: Get today's games
  async getTodayGames(league = null, timezone = null) {
    const today = new Date().toISOString().split('T')[0];
    return await this.getGamesByDate(today, league, null, timezone);
  }

  // Convenience method: Get games for a specific team
  async getTeamGames(teamId, season = null) {
    const params = { team: teamId };
    if (season) params.season = season;
    return await this.getGames(params);
  }

  // Convenience method: Get games for a specific league/season
  async getLeagueGames(league, season, date = null) {
    const params = { league, season };
    if (date) params.date = date;
    return await this.getGames(params);
  }

  // ===== GAME EVENTS =====
  async getGameEvents(gameId) {
    if (!gameId) {
      throw new Error('Game ID is required for events endpoint');
    }
    return await this.makeRequest('/games/events', { game: gameId });
  }

  // ===== STANDINGS =====
  async getStandings(params = {}) {
    return await this.makeRequest('/standings', params);
  }

  // ===== STATISTICS =====
  async getStatistics(params = {}) {
    return await this.makeRequest('/games/statistics', params);
  }

  // ===== HELPER METHODS =====

  // Get current NHL season year in 4-digit format (e.g., 2025 for 2025-26 season)
  // NHL API uses 4-digit year representing the season start year
  getCurrentSeason() {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1; // 1-12

    // NHL season runs Oct-Jun
    // If Jan-Sep, we're in season that started last year
    // If Oct-Dec, we're in season that starts this year
    return currentMonth >= 10 ? currentYear : currentYear - 1;
  }

  // Get today's date in YYYY-MM-DD format
  getTodayDate() {
    return new Date().toISOString().split('T')[0];
  }

  // Format time
  formatTime(datetime) {
    if (!datetime) return '';
    const date = new Date(datetime);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  // Get full date time
  getFullDateTime(datetime) {
    if (!datetime) return '';
    const date = new Date(datetime);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }
}

// Create singleton instance
const nhlService = new NHLService();

// Export as default
export default nhlService;

// Also export class for manual instantiation if needed
export { NHLService };

// Browser debugging helper
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  window.NHLService = {
    instance: nhlService,
    clearCache: () => nhlService.clearCache(),
    setApiKey: (key) => nhlService.setApiKey(key),
    enableDebug: () => { nhlService.debug = true; },
    disableDebug: () => { nhlService.debug = false; }
  };
  console.log('🏒 NHL Service available at window.NHLService');
}
